import { RoomType } from '@core/referential/room-type.enum'
import { UseCase } from '@core/referential/use-case.enum'
import { createFileRoute, redirect } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { z } from 'zod'

import PendingComponent from '@/components/pending-component'
import { Zone } from '@/enum/zone.enum'
import { parseDateRange } from '@/lib/date'
import { isUseCase } from '@/lib/utils'
import { temperatureComfortQuery } from '@/queries/site-energy-temperature-comfort.query'

export const defaultDateRange = parseDateRange({
  startDate: dayjs().subtract(7, 'day').toDate(),
  endDate: dayjs().toDate(),
})

export const SearchSchema = z.object({
  startDate: z.string().default(defaultDateRange.startDate),
  endDate: z.string().default(defaultDateRange.endDate),
  zone: z.nativeEnum(Zone).default(Zone.DAY),
  filterBy: z.enum(['site', 'building', 'floor']).default('site'),
  sites: z.array(z.string()).optional(),
  buildings: z.array(z.string()).optional(),
  floors: z.array(z.string()).optional(),
  roomType: z.enum([RoomType.ROOM, RoomType.OFFICE, 'all']).default('all'),
})

export type Search = z.infer<typeof SearchSchema>

export function useSearch() {
  const navigate = Route.useNavigate()
  const search = Route.useSearch()

  const setSearch = (search: Search) => {
    void navigate({
      search,
      replace: true,
    })
  }

  return { search, setSearch }
}

export const Route = createFileRoute('/_dashboard/comfort/temperature/')({
  validateSearch: SearchSchema,
  async beforeLoad({ search, context: { queryClient } }) {
    if (!isUseCase(UseCase.ENERGY)) {
      throw redirect({
        to: '/403',
        search: { reason: 'wrongUseCase' },
      })
    }

    const temperatureComfort = await queryClient.ensureQueryData(
      temperatureComfortQuery({
        startDate: search.startDate,
        endDate: search.endDate,
      }),
    )

    if (!temperatureComfort || temperatureComfort.length === 0) {
      return
    }

    const siteIds = temperatureComfort.map((site) => site.id)

    if (siteIds.length > 0 && !search.sites && search.filterBy === 'site') {
      throw redirect({
        to: '/comfort/temperature',
        search: {
          ...search,
          sites: siteIds,
          buildings: undefined,
          floors: undefined,
        },
        throw: false,
      })
    }
  },
  pendingComponent: PendingComponent,
})
